import { useNavigation } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, Text, View } from 'react-native';
import { JourneyCard } from '~/components/JourneyCard';
import { Screen } from '~/components/Screen';
import { BackHeader, Notice, TextButton } from '~/components/ui';
import type { JobListItem } from '~/lib/api';
import { api } from '~/lib/api';
import { useTheme } from '~/theme/ThemeProvider';
import { JobDetailScreen } from './JobDetailScreen';

/** Full job history: paginated, status per row, tap into the detail. */
export function JobsScreen() {
  const { c, t, space, touch } = useTheme();
  const nav = useNavigation<{ goBack: () => void }>();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async (cur?: string, append = false) => {
    setLoading(true);
    try {
      const resp = await api.listJobs(25, cur);
      setJobs((prev) => (append ? [...prev, ...resp.jobs] : resp.jobs));
      setCursor(resp.next_cursor);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (selected) {
    return <JobDetailScreen jobId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <Screen>
      <BackHeader
        title="Jobs"
        onBack={() => nav.goBack()}
        backLabel="Back to Recent journeys"
        right={
          <TextButton
            testID="jobs-refresh"
            label={loading ? 'Loading' : 'Refresh'}
            onPress={() => void load()}
          />
        }
      />

      {error ? (
        <View style={{ paddingHorizontal: space.md, paddingTop: space.md }}>
          <Notice tone="stateFailed" title="Could not load jobs.">
            {error}
          </Notice>
        </View>
      ) : null}

      <FlatList
        data={jobs}
        keyExtractor={(j) => j.job_id}
        contentContainerStyle={{ padding: space.md, gap: space.sm }}
        renderItem={({ item }) => (
          <JourneyCard
            job={item}
            testID={`job-${item.job_id}`}
            onPress={() => setSelected(item.job_id)}
          />
        )}
        ListEmptyComponent={
          !loading && !error ? (
            <Text style={[t.body, { color: c.dim, textAlign: 'center', marginTop: space.xl }]}>
              No jobs yet. Submit an inspection and it will appear here.
            </Text>
          ) : null
        }
        ListFooterComponent={
          cursor ? (
            <Pressable
              testID="jobs-more"
              accessibilityRole="button"
              onPress={() => void load(cursor, true)}
              disabled={loading}
              style={{ alignItems: 'center', minHeight: touch.min, justifyContent: 'center' }}
            >
              {loading ? (
                <ActivityIndicator color={c.primary} />
              ) : (
                <Text style={[t.label, { color: c.text }]}>Load more</Text>
              )}
            </Pressable>
          ) : null
        }
      />
    </Screen>
  );
}
